import { FC } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { SubtextLine, TimelineEndColor } from './customerCareTimelineUtils'

interface TimelineSectionProps {
  time: string
  title: string
  detail?: string | SubtextLine[]
  endColor?: TimelineEndColor
  showConnector: boolean
  'data-testid'?: string
}

const END_COLOR_TOKEN: Record<TimelineEndColor, string> = {
  green: 'var(--shape-confirm)',
  red: 'var(--shape-error)'
}

const DETAIL_TEXT_SX = { color: 'var(--text-secondary)', fontSize: 'var(--size-xs)', lineHeight: 'var(--line-height-xs)' }

export const TimelineSection: FC<TimelineSectionProps> = ({ time, title, detail, endColor, showConnector, 'data-testid': dataTestId }) => {
  const dotColor = endColor && END_COLOR_TOKEN[endColor]
  const detailLines = Array.isArray(detail) ? detail : undefined
  const detailText = typeof detail === 'string' ? detail : undefined

  return (
    <Stack direction="row" sx={{ alignItems: 'flex-start', width: '100%' }} data-testid={dataTestId}>
      <Box sx={{ width: '82px', flexShrink: 0, paddingTop: 'var(--spacing-xs)' }}>
        <Typography sx={{ color: 'var(--text-disabled)', fontSize: 'var(--size-2xs)', fontWeight: 500, whiteSpace: 'nowrap' }}>{time}</Typography>
      </Box>
      <Stack sx={{ alignItems: 'center', alignSelf: 'stretch', width: '22px', flexShrink: 0, paddingTop: 'var(--spacing-s)' }}>
        <Box
          sx={{
            width: 'var(--spacing-xs)',
            height: 'var(--spacing-xs)',
            flexShrink: 0,
            borderRadius: '50%',
            backgroundColor: dotColor || 'var(--bg-surface)',
            border: `2px solid ${dotColor || 'var(--border-high)'}`
          }}
        />
        {showConnector && <Box sx={{ width: '2px', flex: '1 0 0', minHeight: '1px', backgroundColor: 'var(--border-low)' }} />}
      </Stack>
      <Stack
        sx={{
          flex: '1 0 0',
          minWidth: 0,
          gap: '3px',
          paddingTop: 'var(--spacing-2xs)',
          paddingLeft: 'var(--spacing-2xs)',
          paddingBottom: 'var(--spacing-m)'
        }}
      >
        <Typography sx={{ color: 'var(--text-primary)', fontSize: 'var(--size-s)', fontWeight: 500, lineHeight: 'var(--line-height-s)' }}>
          {title}
        </Typography>
        {detailText && <Typography sx={DETAIL_TEXT_SX}>{detailText}</Typography>}
        {detailLines?.map(({ label, text }) => (
          <Typography key={label ?? text} sx={DETAIL_TEXT_SX}>
            {label && <Box component="span" sx={{ fontWeight: 500 }}>{`${label} `}</Box>}
            {text}
          </Typography>
        ))}
      </Stack>
    </Stack>
  )
}

TimelineSection.displayName = 'TimelineSection'
